import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formStatusSchema, formsResponseSchema, type IssuanceForm } from '@unit/shared';
import { useApi } from '../../lib/useApi.js';
import { ApiError } from '../../components/ApiError.js';
import { useClaimRoles } from '../auth/useClaimRoles.js';

/**
 * Forms (REQUIREMENTS §2.6). For a commander: "pending signatures across the
 * unit — who has not signed yet". For a soldier: every form of theirs. The
 * server scopes the list; this screen only filters by status.
 */
export function FormsPage() {
  const { t, i18n } = useTranslation();
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? 'pending_signature';
  const { roles } = useClaimRoles();
  const soldierOnly = roles.every((r) => r === 'soldier');

  const q = new URLSearchParams();
  if (status) q.set('status', status);
  const state = useApi(`/forms?${q.toString()}`, formsResponseSchema);

  return (
    <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-900">
          {soldierOnly ? t('forms.myTitle') : t('forms.unitTitle')}
        </h2>
        <select
          value={status}
          onChange={(e) => {
            const next = new URLSearchParams(params);
            if (e.target.value) next.set('status', e.target.value);
            else next.delete('status');
            setParams(next);
          }}
          className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
          data-testid="forms-status"
        >
          <option value="">{t('forms.allStatuses')}</option>
          {formStatusSchema.options.map((s) => (
            <option key={s} value={s}>{t(`formStatus.${s}`)}</option>
          ))}
        </select>
      </div>

      {state.kind === 'loading' && <p className="mt-3 text-slate-500">{t('home.loading')}</p>}
      {state.kind === 'error' && (
        <div className="mt-3"><ApiError code={state.code} requestId={state.requestId} /></div>
      )}
      {state.kind === 'ready' && state.data.forms.length === 0 && (
        <p className="mt-3 text-slate-500" data-testid="forms-empty">{t('forms.empty')}</p>
      )}
      {state.kind === 'ready' && state.data.forms.length > 0 && (
        <ul className="mt-4 divide-y divide-slate-100" data-testid="forms-list">
          {state.data.forms.map((form) => (
            <FormRow key={form.id} form={form} language={i18n.language} showSoldier={!soldierOnly} />
          ))}
        </ul>
      )}
    </section>
  );
}

export function FormRow({ form, language, showSoldier }: { form: IssuanceForm; language: string; showSoldier: boolean }) {
  const { t } = useTranslation();
  return (
    <li data-testid={`form-${form.id}`}>
      <Link to={`/forms/${form.id}`} className="flex flex-wrap items-center justify-between gap-2 py-3 hover:bg-slate-50">
        <span>
          <span className="font-medium text-slate-900">
            {showSoldier ? form.soldierName : t('forms.fromIssuer', { issuer: form.issuerName })}
          </span>
          <span className="ms-2 text-sm text-slate-500">{t('forms.itemCount', { count: form.lineItems.length })}</span>
        </span>
        <span className="flex items-center gap-3 text-xs">
          <StatusPill status={form.status} />
          <span className="latin-inline text-slate-400">{formatDate(form.issuedAt, language)}</span>
        </span>
      </Link>
    </li>
  );
}

export function StatusPill({ status }: { status: IssuanceForm['status'] }) {
  const { t } = useTranslation();
  const tone =
    status === 'pending_signature' ? 'bg-amber-50 text-amber-800 ring-amber-200'
    : status === 'signed' ? 'bg-emerald-50 text-emerald-800 ring-emerald-200'
    : 'bg-slate-100 text-slate-600 ring-slate-200';
  return (
    <span className={`rounded px-2 py-0.5 ring-1 ${tone}`} data-testid="form-status">
      {t(`formStatus.${status}`)}
    </span>
  );
}

/**
 * DD/MM/YYYY for Hebrew (§2.8 names the format), the locale's own for English.
 * Built by hand for Hebrew: Intl's he-IL output is 12.09.2026, dots, which is
 * not what the requirement says and not what the unit's paperwork uses.
 */
export function formatDate(iso: string | null, language: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (!language.startsWith('en')) {
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${d.getFullYear()}`;
  }
  return d.toLocaleDateString(language, { day: '2-digit', month: '2-digit', year: 'numeric' });
}
